import { InjectionToken, Type } from '@angular/core';
import { ResourcePropertyDetail } from './resource-property-detail.model';

/**
 * The inputs a component rendering the properties of one resource category has to accept.
 *
 * Not implemented by those components: their inputs are signals and therefore not assignable
 * to the plain properties declared here. The interface types the object passed to
 * {@code ngComponentOutlet} instead, which keeps both sides in sync.
 */
export interface HisResourcePropertyDisplayInputs {
    /** Only present so that the interface types the {@code inputs} of {@code ngComponentOutlet}. */
    [input: string]: unknown;

    /** The properties of the category, including the translations delivered by the server. */
    details: ResourcePropertyDetail[];
}

/**
 * A built-in rendering for the properties of one resource category, used instead of the
 * generic key/value list of {@code <his-select-file />}.
 *
 * Both components receive the inputs declared by {@link HisResourcePropertyDisplayInputs}.
 * A component has to be declared in (and reachable through) a loaded {@code NgModule}; it is
 * instantiated by {@code ngComponentOutlet}.
 *
 * This is the Angular counterpart of the JSF {@code selectFile-metadata-icon.xhtml} and
 * {@code selectFile-metadata.xhtml}, which switch on the category key instead.
 */
export interface HisResourcePropertyDisplay {
    /** The {@link ResourcePropertyDetail.categoryKey} this display is responsible for. */
    categoryKey: string;

    /** Rendered next to the filename; omitted when the category needs no icon. */
    icon?: Type<unknown>;

    /** Rendered inside the properties overlay; omitted when the generic list suffices. */
    details?: Type<unknown>;

    /**
     * Whether the category is included in the general properties overlay. Defaults to
     * {@code true}. Set to {@code false} for a category presented exclusively by its icon.
     */
    showInOverlay?: boolean;
}

/**
 * Registers additional {@link HisResourcePropertyDisplay}s for the resource categories of an
 * application. Entries provided here take precedence over the ones shipped with cs.sys.hisng:
 *
 * ```ts
 * { provide: HIS_RESOURCE_PROPERTY_DISPLAYS, multi: true, useValue: { categoryKey: 'myCategory', icon: MyIconComponent } }
 * ```
 */
export const HIS_RESOURCE_PROPERTY_DISPLAYS = new InjectionToken<HisResourcePropertyDisplay[]>( 'HIS_RESOURCE_PROPERTY_DISPLAYS' );

/**
 * The untranslated values of the given properties, keyed by their untranslated property key —
 * the shape a {@link HisResourcePropertyDisplay} switches on.
 *
 * Mirrors the {@code properties} parameter the JSF component hands to its metadata templates.
 */
export function resourcePropertyMap( details: ResourcePropertyDetail[] ): Map<string, string> {
    return new Map( details.map( ( detail ) => [detail.propertyKey, detail.propertyValue] ) );
}
